import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, type AdminAccountListItem } from '@/services/api';
import { Loader2, ShieldPlus, X } from 'lucide-react';

export interface AdminFormModalProps {
  admin?: AdminAccountListItem | null;
  onClose: () => void;
  onSaved?: () => void;
}

export function AdminFormModal({ admin, onClose, onSaved }: AdminFormModalProps) {
  const isEdit = !!admin;
  const [email, setEmail] = useState(admin?.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setEmail(admin?.email || '');
    setPassword('');
    setConfirmPassword('');
    setError('');
  }, [admin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Укажите email');
      return;
    }
    if (!isEdit && !password) {
      setError('Укажите пароль');
      return;
    }
    if (password || confirmPassword) {
      if (password.length < 8) {
        setError('Пароль должен быть не менее 8 символов');
        return;
      }
      if (password !== confirmPassword) {
        setError('Пароли не совпадают');
        return;
      }
    }

    setIsLoading(true);
    try {
      if (isEdit && admin) {
        await api.updateAdmin(admin.id, { email: email.trim(), password: password || undefined });
      } else {
        await api.createAdmin(email.trim(), password);
      }
      onSaved?.();
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Не удалось сохранить администратора';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Редактировать администратора' : 'Новый администратор'}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) onClose();
      }}
    >
      <div className="bg-background rounded-lg border shadow-lg w-full max-w-md relative">
        <button
          type="button"
          onClick={onClose}
          disabled={isLoading}
          aria-label="Закрыть"
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="p-6 text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-3">
            <ShieldPlus className="w-6 h-6 text-primary" />
          </div>
          <h2 className="text-xl font-semibold">{isEdit ? 'Редактировать администратора' : 'Новый администратор'}</h2>
          {isEdit && (
            <p className="text-sm text-muted-foreground mt-1">Оставьте пароль пустым, чтобы не менять его</p>
          )}
        </div>
        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="admin-form-email">Email</Label>
            <Input
              id="admin-form-email"
              type="email"
              placeholder="admin@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-form-password">{isEdit ? 'Новый пароль' : 'Пароль'}</Label>
            <Input
              id="admin-form-password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required={!isEdit}
              minLength={8}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-form-confirm">Подтвердите пароль</Label>
            <Input
              id="admin-form-confirm"
              type="password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              required={!isEdit}
              minLength={8}
            />
          </div>
          {error && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">{error}</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Отмена
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Сохранить
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
